import {
  DateFieldComparison,
  ExperienceCategory,
  ExperienceFilter,
  ExperienceMode,
  ExperienceStatus,
  ExperienceType,
  IdFilterComparison,
} from '@/generated/graphql';
import { lte } from 'lodash';
import { DateTime } from 'luxon';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { useGetExperiencesHappeningNowIds } from './use-get-experiences-happening-now';
import { gql, useQuery } from '@apollo/client';

export const EXPLORE_FILTER_CATEGORY = 'category';
export const EXPLORE_FILTER_TYPE = 'type';
export const EXPLORE_FILTER_TIME = 'time';
export const EXPLORE_FILTER_MODE = 'mode';
export const EXPLORE_FILTER_LOCALITY = 'locality';
export const EXPLORE_FILTER_TAG = 'tag';

export const exploreTypeFilterMap = {
  [ExperienceType.Box.toLocaleLowerCase()]: ExperienceType.Box,
  [ExperienceType.Event.toLocaleLowerCase()]: ExperienceType.Event,
  [ExperienceType.Schedule.toLocaleLowerCase()]: ExperienceType.Schedule,
  [ExperienceType.Video.toLocaleLowerCase()]: ExperienceType.Video,
};

export const getExploreTypeFilter = (typeFilter?: string | null) => {
  return exploreTypeFilterMap[(typeFilter || '').toLocaleLowerCase()];
};

export const exploreCategoryFilterMap = {
  [ExperienceCategory.Taste.toLocaleLowerCase()]: ExperienceCategory.Taste,
  [ExperienceCategory.Make.toLocaleLowerCase()]: ExperienceCategory.Make,
  [ExperienceCategory.Move.toLocaleLowerCase()]: ExperienceCategory.Move,
  [ExperienceCategory.Connect.toLocaleLowerCase()]: ExperienceCategory.Connect,
  [ExperienceCategory.Explore.toLocaleLowerCase()]: ExperienceCategory.Explore,
  [ExperienceCategory.Advocate.toLocaleLowerCase()]: ExperienceCategory.Advocate,
};

export const getExploreCategoryFilter = (exploreFilter?: string | null) => {
  return exploreCategoryFilterMap[(exploreFilter || '').toLocaleLowerCase()];
};

export enum TimeFilter {
  Today = 'Today',
  ThisWeek = 'ThisWeek',
  NextWeek = 'NextWeek',
  ThisMonth = 'ThisMonth',
  NextMonth = 'NextMonth',
}

export const exploreTimeFilterMap = {
  [TimeFilter.Today.toLocaleLowerCase()]: TimeFilter.Today,
  [TimeFilter.ThisWeek.toLocaleLowerCase()]: TimeFilter.ThisWeek,
  [TimeFilter.NextWeek.toLocaleLowerCase()]: TimeFilter.NextWeek,
  [TimeFilter.ThisMonth.toLocaleLowerCase()]: TimeFilter.ThisMonth,
  [TimeFilter.NextMonth.toLocaleLowerCase()]: TimeFilter.NextMonth,
};

export const getExploreTimeFilter = (exploreTimeFilter?: string | null) => {
  return exploreTimeFilterMap[(exploreTimeFilter || '').toLocaleLowerCase()];
};

export const timeFilterLabels = {
  [TimeFilter.Today]: 'Today',
  [TimeFilter.ThisWeek]: 'This week',
  [TimeFilter.NextWeek]: 'Next week',
  [TimeFilter.ThisMonth]: 'This month',
  [TimeFilter.NextMonth]: 'Next month',
};

export const exploreModeFilterMap = {
  [ExperienceMode.Online.toLocaleLowerCase()]: ExperienceMode.Online,
  [ExperienceMode.InPerson.toLocaleLowerCase()]: ExperienceMode.InPerson,
};

export const getExploreModeFilter = (exploreModeFilter?: string | null) => {
  return exploreModeFilterMap[(exploreModeFilter || '').toLocaleLowerCase()];
};

export const modeFilterLabels = {
  [ExperienceMode.Online]: 'Online',
  [ExperienceMode.InPerson]: 'In person',
};

export const getExploreLocalityFilter = (localityFilter?: string | null) => {
  return localityFilter || '';
};

export const getExploreTagFilter = (tagFilter?: string | null) => {
  return tagFilter || '';
};

interface ExperienceFilterReturnProps {
  filter: ExperienceFilter;
  timeFilter?: DateFieldComparison;
  isFilterSet: boolean;
  category?: ExperienceCategory;
  type?: ExperienceType;
  time?: TimeFilter;
  mode?: ExperienceMode;
  locality?: string;
  tag?: string;
}

const getTimeFilter = (filter?: TimeFilter): DateFieldComparison | undefined => {
  if (!filter) {
    return;
  }
  const now = DateTime.utc().startOf('hour');
  switch (filter) {
    case TimeFilter.Today:
      return { between: { lower: now.toISO(), upper: now.plus({ days: 1 }).toISO() } };
    case TimeFilter.ThisWeek:
      return { between: { lower: now.toISO(), upper: now.plus({ weeks: 1 }).toISO() } };
    case TimeFilter.NextWeek:
      return {
        between: { lower: now.plus({ weeks: 1 }).toISO(), upper: now.plus({ weeks: 2 }).toISO() },
      };
    case TimeFilter.ThisMonth:
      return { between: { lower: now.toISO(), upper: now.plus({ months: 1 }).toISO() } };
    case TimeFilter.NextMonth:
      return {
        between: { lower: now.plus({ months: 1 }).toISO(), upper: now.plus({ months: 2 }).toISO() },
      };
  }
};

export const useExperienceFilter = (): ExperienceFilterReturnProps => {
  const { ids: happeningNowIds } = useGetExperiencesHappeningNowIds();
  const searchParams = useSearchParams();
  const queryCategoryFilter = getExploreCategoryFilter(searchParams?.get(EXPLORE_FILTER_CATEGORY));
  const queryTypeFilter = getExploreTypeFilter(searchParams?.get(EXPLORE_FILTER_TYPE));
  const queryTimeFilter = getExploreTimeFilter(searchParams?.get(EXPLORE_FILTER_TIME));
  const queryModeFilter = getExploreModeFilter(searchParams?.get(EXPLORE_FILTER_MODE));
  const queryLocalityFilter = getExploreLocalityFilter(searchParams?.get(EXPLORE_FILTER_LOCALITY));
  const queryTagFilter = getExploreTagFilter(searchParams?.get(EXPLORE_FILTER_TAG));
  const isFilterSet = !!(
    queryCategoryFilter ||
    queryTypeFilter ||
    queryTimeFilter ||
    queryModeFilter ||
    queryLocalityFilter ||
    queryTagFilter
  );

  const typeFilter = queryTypeFilter
    ? { type: { eq: queryTypeFilter } }
    : { type: { neq: ExperienceType.Box } };
  const categoryFilter = queryCategoryFilter ? { category: { eq: queryCategoryFilter } } : {};
  const timeFilter = getTimeFilter(queryTimeFilter);
  const modeFilter = queryModeFilter ? { mode: { eq: queryModeFilter } } : {};
  const localityFilter = queryLocalityFilter ? { locality: { eq: queryLocalityFilter } } : {};
  const now = new Date();
  const currentDate = DateTime.fromJSDate(now).toISO();
  //todo: add province filter
  const timeFilterArray = [
    ...(happeningNowIds && happeningNowIds.length > 0
      ? [
          {
            or: [
              {
                id: { in: happeningNowIds },
              },
            ],
          },
        ]
      : []),
    {
      and: [
        {
          type: { eq: ExperienceType.Schedule },
        },
        {
          nextAvailability: timeFilter,
        },
        {
          nextAvailability: { isNot: null },
        },
      ],
    },
    {
      and: [
        {
          type: { eq: ExperienceType.Event },
        },
        {
          nextEvent: timeFilter,
        },
        {
          nextEvent: { isNot: null },
        },
      ],
    },
    {
      type: { eq: ExperienceType.Box },
    },
  ];
  const defaultTimeFilterArray = useMemo(
    () => [
      ...(happeningNowIds && happeningNowIds.length > 0
        ? [
            {
              or: [
                {
                  id: { in: happeningNowIds },
                },
              ],
            },
          ]
        : []),
      {
        and: [
          {
            type: { eq: ExperienceType.Schedule },
          },
          {
            nextAvailability: { gt: DateTime.now().toISO() },
          },
          {
            nextAvailability: { isNot: null },
          },
        ],
      },
      {
        and: [
          {
            type: { eq: ExperienceType.Event },
          },
          {
            nextEvent: { gt: DateTime.now().toISO() },
          },
          {
            nextEvent: { isNot: null },
          },
        ],
      },
      {
        type: { eq: ExperienceType.Box },
      },
    ],
    [happeningNowIds],
  );
  const filter: ExperienceFilter = {
    status: { eq: ExperienceStatus.Publish },
    ...typeFilter,
    ...categoryFilter,
    ...modeFilter,
    ...localityFilter,
    or: timeFilter ? timeFilterArray : defaultTimeFilterArray,
  };

  // If a tag query param is present, fetch experience IDs for that tag and restrict the
  // experience filter to those IDs. This ensures paging and sorting happen server-side.
  // We perform a lightweight GraphQL query to the experienceTags connection filtered by tag title.
  const GET_EXPERIENCE_IDS_BY_TAG = gql`
    query ExperienceTagByTag($filter: ExperienceTagFilter) {
      experienceTags(filter: $filter, paging: { first: 500 }) {
        edges {
          node {
            experience {
              id
            }
          }
        }
      }
    }
  `;

  const { data: tagData } = useQuery(GET_EXPERIENCE_IDS_BY_TAG, {
    variables: {
      filter: queryTagFilter ? { tag: { title: { eq: queryTagFilter } } } : undefined,
    },
    skip: !queryTagFilter,
    fetchPolicy: 'cache-first',
  });

  const tagExperienceIds: string[] = useMemo(() => {
    if (!tagData) return [];
    return Array.from(
      new Set(
        tagData.experienceTags.edges
          .map((e: any) => e?.node?.experience?.id)
          .filter((id: string | null | undefined) => id != null),
      ),
    );
  }, [tagData]);

  // If tag filter provided, narrow the results to experiences with those ids.
  const finalFilter: ExperienceFilter = useMemo(() => {
    if (queryTagFilter) {
      // If we have ids, filter by them. If none were found, use an impossible id to
      // ensure zero results instead of returning everything.
      if (tagExperienceIds.length > 0) {
        return {
          ...filter,
          id: { in: tagExperienceIds },
        };
      }
      return {
        ...filter,
        id: { in: ['__NO_MATCH__'] } as IdFilterComparison,
      };
    }
    return filter;
  }, [filter, queryTagFilter, tagExperienceIds]);
  return {
    filter: finalFilter,
    timeFilter,
    isFilterSet,
    category: queryCategoryFilter,
    type: queryTypeFilter,
    time: queryTimeFilter,
    mode: queryModeFilter,
    locality: queryLocalityFilter,
    tag: queryTagFilter,
  };
};
